import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// COMPONENTS
import TariffLineSingleView from './plan/line/TariffLineSingleView';
import TariffPlanList from './plan/TariffPlanTable';
import TariffPlanSingle from './plan/TariffPlanSingleView';
import Loader from '../../common/Loader';

// NETWORK
import useTariffRequest from '../../../requests/TariffRequest';

const TariffingSingleView = ({
  tariffScheme,
  backButtonClick,
  isNew = false,
  errorHandler,
  selectedPlanId,
  showTariffPlan = false,
  showTariffLine = false,
  setShowTariffLine = null,
  setShowTariffPlan = null,
  addNewTariffPlan = false,
  setAddNewTariffPlan = null,
  setAddNewTariffLine = null,
  addNewTariffLine = false,
  isEditing = false,
  setIsEditing = () => {},
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [tariffPlans, setTariffPlans] = useState([]);
  const [showSingleTariffPlan, setShowSingleTariffPlan] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentTariffScheme, setCurrentTariffScheme] = useState(
    tariffScheme || {}
  );
  const [tariffPlan, setTariffPlan] = useState(null);
  const [tariffLine, setTariffLine] = useState(null);
  const {
    getSchemeStatusTypes,
    getTariffScheme,
    getTariffPlans,
    createNewTariffScheme,
    updateTariffScheme,
    cancelTariffSchemeId,
  } = useTariffRequest();

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);

      const schemeStatusesAndTypes = await getSchemeStatusTypes(errorHandler);

      setTypes(schemeStatusesAndTypes.scheme_types);
      setStatuses(schemeStatusesAndTypes.scheme_statuses);

      if (tariffScheme?.tariff_scheme_id) {
        const [updatedScheme, tariffPlans] = await Promise.all([
          getTariffScheme(tariffScheme.tariff_scheme_id, errorHandler),
          getTariffPlans(tariffScheme.tariff_scheme_id, errorHandler),
        ]);

        setCurrentTariffScheme(updatedScheme);
        setTariffPlans(tariffPlans);

        if (selectedPlanId) {
          const selectedPlan = tariffPlans.find(
            (plan) => plan.tariff_plan_id === selectedPlanId
          );
          if (selectedPlan) {
            setShowSingleTariffPlan(selectedPlan);
          }
        }
      }

      setIsLoading(false);
    };

    fetchInitialData();
  }, [tariffScheme, selectedPlanId, errorHandler]);

  useEffect(() => {
    if (addNewTariffPlan) {
      setShowTariffPlan(true);
    }
  }, [addNewTariffPlan]);

  const handleBackClick = async (
    refreshFlag,
    showTariffPlanFlag = false,
    showTariffLineFlag = false
  ) => {
    if (isEditing && tariffPlan) {
      toggleEdit();
      return;
    }

    if (isEditing && !tariffPlan && !showTariffPlanFlag) {
      setShowTariffPlan(false);
      setAddNewTariffPlan(false);
      toggleEdit();
      return;
    }

    if (showTariffLineFlag) {
      if (typeof setShowTariffLine === 'function') {
        setShowTariffLine(false);
      }
      if (typeof setShowTariffPlan === 'function') {
        setShowTariffPlan(true);
      }
      setAddNewTariffLine(false);
      setTariffLine(null);
      backButtonClick(refreshFlag, false, true);
      return;
    }

    if (showTariffPlanFlag) {
      if (typeof setShowTariffPlan === 'function') {
        setShowTariffPlan(true);
      }
      if (typeof setShowTariffLine === 'function') {
        setShowTariffLine(false);
      }
      setAddNewTariffLine(false);
      setTariffLine(null);
      backButtonClick(refreshFlag, true, false);
      return;
    }

    // When going back from TariffPlanSingleView, we need to reset the state
    if (typeof setShowTariffLine === 'function') {
      setShowTariffLine(false);
    }
    if (typeof setShowTariffPlan === 'function') {
      setShowTariffPlan(false);
    }
    setAddNewTariffLine(false);
    setAddNewTariffPlan(false);
    setTariffLine(null);
    setIsEditing(false);
    if (!tariffScheme) {
      setShowSingleTariffPlan(null);
    }

    if (refreshFlag) {
      setIsLoading(true);
      const [updatedScheme, tariffPlans] = await Promise.all([
        getTariffScheme(tariffScheme.tariff_scheme_id, errorHandler),
        getTariffPlans(tariffScheme.tariff_scheme_id, errorHandler),
      ]);

      setCurrentTariffScheme(updatedScheme);
      setTariffPlans(tariffPlans);
      setIsLoading(false);
    }

    // Only call backButtonClick if we're not in the scheme view
    if (!tariffScheme) {
      backButtonClick(refreshFlag, false, false);
    }
  };

  const handleNewTariffPlanClick = () => {
    setAddNewTariffPlan(true);
    setShowTariffPlan(true);
    // setIsEditing(true);
    setTariffPlan(null);
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    setEdits((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isNew) {
      if (!edits.tariff_scheme_name?.trim()) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Scheme Name.');
        return;
      }
      if (!edits.scheme_type) {
        setHasError(true);
        setErrorMessage('Select a Scheme Type.');
        return;
      }
      if (!edits.scheme_status) {
        setHasError(true);
        setErrorMessage('Select a Scheme Status.');
        return;
      }

      setIsSaving(true);
      setHasError(false);
      setErrorMessage('');

      const result = await createNewTariffScheme(edits, errorHandler);

      if (!result.error) {
        backButtonClick(true);
      } else {
        setIsSaving(false);
      }
    } else {
      if (
        typeof edits.tariff_scheme_name !== 'undefined' &&
        !edits.tariff_scheme_name?.trim()
      ) {
        setHasError(true);
        setErrorMessage('Requires a Tariff Scheme Name.');
        return;
      }

      setIsSaving(true);
      setHasError(false);
      setErrorMessage('');

      const updateData = { ...edits };
      if (
        updateData.tariff_scheme_name === currentTariffScheme.tariff_scheme_name
      ) {
        delete updateData.tariff_scheme_name;
      }

      const result = await updateTariffScheme(
        currentTariffScheme.tariff_scheme_id,
        updateData,
        errorHandler
      );

      if (!result.error) {
        setIsEditing(false);
        // Fetch updated data immediately
        setIsLoading(true);
        const [updatedScheme, tariffPlans] = await Promise.all([
          getTariffScheme(currentTariffScheme.tariff_scheme_id, errorHandler),
          getTariffPlans(currentTariffScheme.tariff_scheme_id, errorHandler),
        ]);
        setCurrentTariffScheme(updatedScheme);
        setTariffPlans(tariffPlans);
        setIsLoading(false);
        // Reset edits state
        setEdits({});
      } else {
        setIsSaving(false);
      }
    }
  };

  const handleCancelScheme = async () => {
    setIsSaving(true);
    setShowConfirmation(false);

    await cancelTariffSchemeId(tariffScheme.tariff_scheme_id, errorHandler);

    backButtonClick(true);
  };

  const handleCancelClick = () => {
    if (isNew) {
      backButtonClick();
    } else {
      toggleEdit();
    }
  };

  const renderTypeSelect = () => (
    <Select
      name="scheme_type"
      value={
        isNew
          ? edits.scheme_type || ''
          : edits.scheme_type || currentTariffScheme.scheme_type || ''
      }
      onChange={handleFormChange}
      fullWidth
      size="small"
      labelId="scheme_type-label-id"
      required
      displayEmpty
    >
      {isNew && (
        <MenuItem value="" disabled>
          Select One
        </MenuItem>
      )}
      {types.map((type) => (
        <MenuItem key={type.code} value={type.code}>
          {type.meaning}
        </MenuItem>
      ))}
    </Select>
  );

  const renderStatusSelect = () => (
    <FormControl fullWidth>
      <Select
        name="scheme_status"
        value={
          isNew
            ? edits.scheme_status || ''
            : edits.scheme_status || currentTariffScheme.scheme_status || ''
        }
        onChange={handleFormChange}
        fullWidth
        labelId="status-label-id"
        size="small"
        required
        displayEmpty
      >
        {isNew && (
          <MenuItem value="" disabled>
            Select One
          </MenuItem>
        )}
        {statuses.map((status) => (
          <MenuItem key={status.code} value={status.code}>
            {status.meaning}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const generateFormGroup = (label, displayValue) => {
    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong> {displayValue}
        </Typography>
      </Grid>
    );
  };

  const renderForm = () => {
    if (isNew || isEditing) {
      return (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800 }}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Name
                </InputLabel>
                <TextField
                  fullWidth
                  name="tariff_scheme_name"
                  required
                  defaultValue={
                    isNew ? '' : currentTariffScheme.tariff_scheme_name
                  }
                  onChange={handleFormChange}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel
                  sx={{ minWidth: 100 }}
                  id="scheme_type-label-id"
                  required
                >
                  Type
                </InputLabel>
                {renderTypeSelect()}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel
                  sx={{ minWidth: 100 }}
                  id="status-label-id"
                  required
                >
                  Status
                </InputLabel>
                {renderStatusSelect()}
              </Stack>
            </Grid>
          </Grid>

          {hasError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => handleCancelClick()}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <Grid container spacing={2}>
          {generateFormGroup('Name:', currentTariffScheme.tariff_scheme_name)}
          {generateFormGroup('Type:', currentTariffScheme.scheme_type_name)}
          {generateFormGroup('Status:', currentTariffScheme.scheme_status_name)}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button color="secondary" variant="contained" onClick={toggleEdit}>
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setShowConfirmation(true)}
          >
            Cancel Scheme
          </Button>
        </Box>

        {!isLoading && (
          <Box sx={{ mt: 4 }}>
            <TariffPlanList
              tariffPlans={tariffPlans}
              showSingleTariffPlan={(plan) => {
                setShowSingleTariffPlan(plan);
                if (setShowTariffPlan) setShowTariffPlan(true);
                if (setShowTariffLine) setShowTariffLine(false);
                setAddNewTariffLine(false);
                setTariffLine(null);
                backButtonClick(false, true, false);
              }}
              addNewTariffPlan={() => handleNewTariffPlanClick()}
              isLoading={isLoading}
            />
          </Box>
        )}
      </Box>
    );
  };

  const generateContent = () => {
    if (isLoading) {
      return <Loader />;
    }

    if (showTariffLine) {
      return (
        <TariffLineSingleView
          tariffPlan={showSingleTariffPlan}
          tariffLine={tariffLine}
          backButtonClick={handleBackClick}
          isNew={addNewTariffLine}
          showTariffLine={showTariffLine}
          errorHandler={errorHandler}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      );
    }

    if (showTariffPlan) {
      return (
        <TariffPlanSingle
          tariffScheme={currentTariffScheme}
          tariffPlan={showSingleTariffPlan}
          setTariffPlan={setTariffPlan}
          setAddNewTariffLine={setAddNewTariffLine}
          backButtonClick={handleBackClick}
          isNew={addNewTariffPlan}
          errorHandler={errorHandler}
          showTariffLine={showTariffLine}
          setShowTariffLine={setShowTariffLine}
          handleParentShowSingleTariffLine={(line) => {
            setTariffLine(line);
            if (setShowTariffLine) setShowTariffLine(true);
            if (setShowTariffPlan) setShowTariffPlan(false);
            setAddNewTariffLine(false);
            backButtonClick(false, false, true);
          }}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      );
    }

    if (addNewTariffPlan) {
      return (
        <TariffPlanSingle
          tariffScheme={currentTariffScheme}
          tariffPlan={showSingleTariffPlan}
          setTariffPlan={setTariffPlan}
          backButtonClick={handleBackClick}
          isNew={true}
          errorHandler={errorHandler}
          showTariffLine={false}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      );
    }

    return renderForm();
  };

  const toggleEdit = () => {
    const newEditingState = !isEditing;
    setIsEditing(newEditingState);
    setEdits({});
    setHasError(false);
    setErrorMessage('');
    // When canceling edit, we need to reset the showTariffPlan and showTariffLine states
    if (!newEditingState) {
      if (typeof setShowTariffLine === 'function') {
        setShowTariffLine(false);
      }
      if (typeof setShowTariffPlan === 'function') {
        setShowTariffPlan(false);
      }
    }
  };

  return (
    <Box>
      {/* Main Content */}
      {generateContent()}

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      >
        <DialogTitle variant="h2">Cancellation Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you would like to cancel the tariff scheme?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmation(false)}
            color="secondary"
            vairant="outlined"
          >
            No
          </Button>
          <Button
            onClick={handleCancelScheme}
            color="secondary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TariffingSingleView;
